import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useJurisprudencias, Jurisprudencia } from '@/hooks/useJurisprudencias';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Plus, Upload, Trash2, Edit, Search, Eye, Bookmark, Scale, FileText, Loader2, Building2, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const JurisprudenciaTab = () => {
  const {
    tribunais, jurisprudencias, stats, isLoading, isImporting, importProgress,
    isMasterAdmin, addTribunal, deleteTribunal, importJsonl,
    deleteJurisprudencia, updateJurisprudencia, refresh,
  } = useJurisprudencias();

  const [tribunalDialog, setTribunalDialog] = useState(false);
  const [tribunalForm, setTribunalForm] = useState({ nome: '', sigla: '', tipo: 'tribunal' });
  const [editDialog, setEditDialog] = useState(false);
  const [editingJuris, setEditingJuris] = useState<Jurisprudencia | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddTribunal = async () => {
    if (!tribunalForm.nome || !tribunalForm.sigla) return;
    const ok = await addTribunal(tribunalForm.nome, tribunalForm.sigla, tribunalForm.tipo);
    if (ok) {
      setTribunalDialog(false);
      setTribunalForm({ nome: '', sigla: '', tipo: 'tribunal' });
    }
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) importJsonl(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEdit = (j: Jurisprudencia) => {
    setEditingJuris(j);
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingJuris) return;
    await updateJurisprudencia(editingJuris.id, {
      ementa: editingJuris.ementa,
      relator: editingJuris.relator,
      orgao_julgador: editingJuris.orgao_julgador,
      area_direito: editingJuris.area_direito,
    });
    setEditDialog(false);
    setEditingJuris(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                Jurispesquisa - Gestão de Jurisprudências
              </CardTitle>
              <CardDescription>
                {isMasterAdmin
                  ? 'Gerencie tribunais, importe jurisprudências e acompanhe estatísticas de uso'
                  : 'Acompanhe estatísticas de uso e visualize jurisprudências importadas'}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={refresh}>
              <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <FileText className="h-8 w-8 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold">{stats?.totalJurisprudencias || 0}</p>
            <p className="text-xs text-muted-foreground">Jurisprudências</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <Building2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold">{stats?.totalTribunais || 0}</p>
            <p className="text-xs text-muted-foreground">Tribunais</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <Eye className="h-8 w-8 mx-auto mb-2 text-purple-500" />
            <p className="text-2xl font-bold">{stats?.totalViews || 0}</p>
            <p className="text-xs text-muted-foreground">Visualizações</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <Bookmark className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
            <p className="text-2xl font-bold">{stats?.totalSaves || 0}</p>
            <p className="text-xs text-muted-foreground">Salvamentos</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <Search className="h-8 w-8 mx-auto mb-2 text-red-500" />
            <p className="text-2xl font-bold">{stats?.totalSearches || 0}</p>
            <p className="text-xs text-muted-foreground">Pesquisas</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* By Tribunal */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Jurisprudências por Tribunal</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.byTribunal && stats.byTribunal.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.byTribunal}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="sigla" fontSize={12} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" name="Total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-8">Nenhum dado disponível</p>
            )}
          </CardContent>
        </Card>

        {/* Top Search Terms */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Palavras Mais Pesquisadas</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.topSearchTerms && stats.topSearchTerms.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.topSearchTerms.slice(0, 6)}
                    dataKey="count"
                    nameKey="term"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ term, count }) => `${term} (${count})`}
                  >
                    {stats.topSearchTerms.slice(0, 6).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-8">Nenhuma pesquisa registrada</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Most Viewed & Most Saved */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Eye className="h-4 w-4" /> Mais Visualizadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.mostViewed && stats.mostViewed.length > 0 ? (
              <div className="space-y-2">
                {stats.mostViewed.map((item, i) => (
                  <div key={item.id} className="flex items-center justify-between text-sm">
                    <span className="truncate flex-1 mr-2">{i + 1}. {item.ementa}...</span>
                    <Badge variant="secondary">{item.views} views</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">Sem dados</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Bookmark className="h-4 w-4" /> Mais Salvas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.mostSaved && stats.mostSaved.length > 0 ? (
              <div className="space-y-2">
                {stats.mostSaved.map((item, i) => (
                  <div key={item.id} className="flex items-center justify-between text-sm">
                    <span className="truncate flex-1 mr-2">{i + 1}. {item.ementa}...</span>
                    <Badge variant="secondary">{item.saves} saves</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">Sem dados</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Master Admin Controls */}
      {isMasterAdmin && (
        <>
          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Ações do Admin Master</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              {/* Add Tribunal */}
              <Dialog open={tribunalDialog} onOpenChange={setTribunalDialog}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-1" /> Adicionar Tribunal
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Novo Tribunal</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Nome</Label>
                      <Input
                        value={tribunalForm.nome}
                        onChange={e => setTribunalForm(f => ({ ...f, nome: e.target.value }))}
                        placeholder="Supremo Tribunal Federal"
                      />
                    </div>
                    <div>
                      <Label>Sigla</Label>
                      <Input
                        value={tribunalForm.sigla}
                        onChange={e => setTribunalForm(f => ({ ...f, sigla: e.target.value.toUpperCase() }))}
                        placeholder="STF"
                      />
                    </div>
                    <div>
                      <Label>Tipo</Label>
                      <Select value={tribunalForm.tipo} onValueChange={v => setTribunalForm(f => ({ ...f, tipo: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="supremo">Supremo</SelectItem>
                          <SelectItem value="superior">Superior</SelectItem>
                          <SelectItem value="regional">Regional</SelectItem>
                          <SelectItem value="estadual">Estadual</SelectItem>
                          <SelectItem value="tribunal">Tribunal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleAddTribunal}>Salvar</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Import JSONL */}
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".jsonl"
                  onChange={handleImport}
                  className="hidden"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isImporting}
                >
                  {isImporting ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-1" />
                  )}
                  Importar .jsonl
                </Button>
                <p className="text-[10px] text-muted-foreground mt-1">Máx: 50MB</p>
              </div>
            </CardContent>

            {isImporting && importProgress.total > 0 && (
              <CardContent className="pt-0">
                <Progress value={(importProgress.current / importProgress.total) * 100} className="h-2" />
                <p className="text-xs text-muted-foreground mt-1">
                  {importProgress.current} / {importProgress.total} linhas processadas
                </p>
              </CardContent>
            )}
          </Card>

          {/* Tribunais List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Tribunais Cadastrados ({tribunais.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {tribunais.map(t => (
                  <Badge key={t.id} variant="outline" className="flex items-center gap-1 px-3 py-1">
                    {t.sigla} - {t.nome}
                    <button onClick={() => deleteTribunal(t.id)} className="ml-1 text-destructive hover:text-destructive/80">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {tribunais.length === 0 && (
                  <p className="text-sm text-muted-foreground">Nenhum tribunal cadastrado</p>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Jurisprudências Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            Jurisprudências Importadas ({jurisprudencias.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tribunal</TableHead>
                <TableHead>Nº Processo</TableHead>
                <TableHead className="min-w-[300px]">Ementa</TableHead>
                <TableHead>Relator</TableHead>
                <TableHead>Área</TableHead>
                <TableHead>Data</TableHead>
                {isMasterAdmin && <TableHead>Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {jurisprudencias.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isMasterAdmin ? 7 : 6} className="text-center text-muted-foreground py-8">
                    Nenhuma jurisprudência importada
                  </TableCell>
                </TableRow>
              ) : (
                jurisprudencias.map(j => (
                  <TableRow key={j.id}>
                    <TableCell>
                      <Badge variant="secondary">{(j as any).tribunais?.sigla || '-'}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{j.numero_processo}</TableCell>
                    <TableCell className="text-xs max-w-[400px] truncate">{j.ementa.substring(0, 150)}...</TableCell>
                    <TableCell className="text-xs">{j.relator || '-'}</TableCell>
                    <TableCell className="text-xs">{j.area_direito || '-'}</TableCell>
                    <TableCell className="text-xs">
                      {j.data_julgamento
                        ? format(new Date(j.data_julgamento), 'dd/MM/yyyy', { locale: ptBR })
                        : '-'}
                    </TableCell>
                    {isMasterAdmin && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => handleEdit(j)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => deleteJurisprudencia(j.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Jurisprudência</DialogTitle>
          </DialogHeader>
          {editingJuris && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label>Ementa</Label>
                <Textarea
                  value={editingJuris.ementa}
                  onChange={e => setEditingJuris({ ...editingJuris, ementa: e.target.value })}
                  rows={4}
                />
              </div>
              <div>
                <Label>Relator</Label>
                <Input
                  value={editingJuris.relator || ''}
                  onChange={e => setEditingJuris({ ...editingJuris, relator: e.target.value })}
                />
              </div>
              <div>
                <Label>Órgão Julgador</Label>
                <Input
                  value={editingJuris.orgao_julgador || ''}
                  onChange={e => setEditingJuris({ ...editingJuris, orgao_julgador: e.target.value })}
                />
              </div>
              <div>
                <Label>Área do Direito</Label>
                <Input
                  value={editingJuris.area_direito || ''}
                  onChange={e => setEditingJuris({ ...editingJuris, area_direito: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveEdit}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JurisprudenciaTab;
